// Item 29: one sorting engine for every widget — multi-level, type-aware,
// custom category order and nulls placement. Levels live on config.sort so
// every existing saved sort (single {key,direction}) keeps working untouched.
import { smartComparator } from "@/lib/filterValues";

export const SORT_DIRECTIONS = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];
export const SORT_NULLS = [
  { value: "last", label: "Nulls last" },
  { value: "first", label: "Nulls first" },
];

const isBlank = (v) => v === null || v === undefined || v === "";

// Normalized levels: [{ key, direction, nulls, custom }]
export function sortLevels(config = {}) {
  return (config.sort || [])
    .map((s) => (s && (s.key || s.field)
      ? {
        key: s.key || s.field,
        direction: s.direction === "asc" ? "asc" : "desc",
        nulls: s.nulls === "first" ? "first" : "last",
        custom: Array.isArray(s.custom) ? s.custom.filter((v) => v !== "" && v != null).map(String) : null,
      }
      : null))
    .filter(Boolean);
}

// What the query engine receives (direction push-down keeps limit/Top-N honest).
export function sortSpecForQuery(config = {}) {
  return sortLevels(config).map((l) => ({ key: l.key, direction: l.direction }));
}

// Fields a sort level can point at: dimensions first, then measures.
export function sortCandidates(config = {}, label) {
  const out = [];
  const push = (key, name, kind) => { if (key && !out.some((o) => o.key === key)) out.push({ key, label: name || key, kind }); };
  (config.dimensions || []).forEach((d) => {
    if (!d) return;
    const key = d.key || d.alias || (d.date_part && d.date_part !== "none" ? `${d.field}_${d.date_part}` : d.field);
    push(key, d.label || (label ? label(d.field) : d.field) || key, "dim");
  });
  (config.measures || []).forEach((m) => {
    if (!m) return;
    const key = m.key || m.alias || (m.field ? `${m.agg || "sum"}_${m.field}` : null);
    push(key, m.label || (m.field ? `${m.agg || "sum"} of ${label ? label(m.field) : m.field}` : key), "meas");
  });
  return out;
}

function comparatorFor(rows, key) {
  const vals = rows.map((r) => r?.[key]).filter((v) => !isBlank(v));
  const numeric = vals.length > 0 && vals.every((v) => typeof v === "number" || (typeof v === "string" && v !== "" && Number.isFinite(Number(v))));
  if (numeric) return (a, b) => Number(a) - Number(b);
  return smartComparator(vals);
}

// Apply the configured sort to a query result ({columns, rows}).
export function applySortEngine(data, config = {}) {
  const levels = sortLevels(config).filter((l) => (data?.columns || []).includes(l.key));
  if (!data || !Array.isArray(data.rows) || !data.rows.length || !levels.length) return data;
  const prepared = levels.map((l) => ({
    ...l,
    cmp: l.custom && l.custom.length ? null : comparatorFor(data.rows, l.key),
    rank: l.custom && l.custom.length
      ? Object.fromEntries(l.custom.map((v, i) => [String(v), i]))
      : null,
  }));
  const rows = [...data.rows].sort((ra, rb) => {
    for (const l of prepared) {
      const a = ra?.[l.key], b = rb?.[l.key];
      const an = isBlank(a), bn = isBlank(b);
      if (an && bn) continue;
      if (an || bn) return (an ? 1 : -1) * (l.nulls === "first" ? -1 : 1);
      let r;
      if (l.rank) {
        const ia = l.rank[String(a)], ib = l.rank[String(b)];
        const va = ia === undefined ? Number.MAX_SAFE_INTEGER : ia;
        const vb = ib === undefined ? Number.MAX_SAFE_INTEGER : ib;
        r = va - vb || String(a).localeCompare(String(b));
      } else {
        r = l.cmp(a, b);
      }
      if (r) return l.direction === "asc" ? r : -r;
    }
    return 0;
  });
  return { ...data, rows };
}
